import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import reorder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ResolveThunks, connect } from "react-redux";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import ADIColors from "@NA/Utils/ADIColors";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Link from "@insite/mobius/Link";
import LazyImage, { LazyImageStyle } from "@insite/mobius/LazyImage";
import { StyledButton } from "@insite/mobius/Clickable";
import OrderDetailsArrowLeft from "@NA/Icons/OrderDetailsArrowLeft";
import ADIOrderDetailsProductInformation from "@NA/Components/OrderDetails/ADIOrderDetailsProductInformation";
import ADIOrderDetailsSummaryTableMobile from "@NA/Components/OrderDetails/ADIOrderDetailsSummaryTableMobile";
import ADIOrderDetailsReorderModal from "@NA/Components/OrderDetails/ADIOrderDetailsReorderModal";
import ADIOrderDetailsReturnItemsModal from "@NA/Components/OrderDetails/ADIOrderDetailsReturnItemsModal";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import WarningRedIcon from "@NA/Icons/WarningRedIcon";
import { getProp } from "@insite/mobius/utilities";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import Hidden from "@insite/mobius/Hidden";
import ADIXtenExportToXtenButton from "@NA/Components/ADIXtenExportToXtenButton";

const mapStateToProps = (state: CustomApplicationState) => {
    const isLoading = state.ADIGlobalData.orderDetailsStore.isLoading;
    let originalString = state.pages.orderDetails.orderNumber;
    let result;
    let parts;
    if (originalString) {
        parts = originalString.split("_");
        result = parts[0];
        if (parts.length > 1) {
            result = parts[0];
        } else {
            result = originalString;
        }
    }

    const order = getOrderState(state, result).value;

    const settingsCollection = getSettingsCollection(state);
    const userRoles = state.context.session.userRoles;
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        order,
        canReorderItems:
            settingsCollection.orderSettings.canReorderItems &&
            ((adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess || adiUserRoles.employee),
        isReordering: state.pages.orderDetails.isReordering,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        allowRMASubmissions: getSettingsCollection(state).customerSettings.allowRMASubmission,
        userRoles,
        isLoading,
    };
};

const mapDispatchToProps = {
    reorder,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ADIOrderDetailsContainerStyles {
    loadingOverlay?: LoadingOverlayProps;
    orderAgainButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    firstSectionBox?: GridContainerProps;
    secondSectionBox?: GridContainerProps;
    thirdSectionBox?: GridContainerProps;
    thirdSectionBoxMobile?: GridContainerProps;
    titleGridContainer?: GridItemProps;
    printGridContainer?: GridItemProps;
    firstSection?: GridItemProps;
    secondSection?: GridItemProps;
    thirdSection?: GridItemProps;
    firstOrderDetailsContainer?: InjectableCss;
    dateContainer?: GridItemProps;
    poNumberContainer?: GridItemProps;
    referenceNumberContainer?: GridItemProps;
    statusContainer?: GridItemProps;
    itemsContainer?: GridItemProps;
    totalContainer?: GridItemProps;
    billingInfoContainer?: GridItemProps;
    shippingInfoContainer?: GridItemProps;
    paymentMethodContainer?: GridItemProps;
    buttonsContainer?: GridItemProps;
    deliveryInfoContainer?: GridItemProps;
    cartsContainer?: GridItemProps;
    priceContainer?: GridItemProps;
    returnItemsButton?: ButtonPresentationProps;
    secondDiv?: InjectableCss;
    firstDiv?: InjectableCss;
    customBorder?: InjectableCss;
    deliveryInfoContainerMultiple?: GridItemProps;
    deliveryInfoContainerMultipleMobile?: GridItemProps;
    rowOne?: InjectableCss;
    firstSectionGridItem?: GridItemProps;
    itemsMainContainer?: GridContainerProps;
    hiddenMobile?: InjectableCss;
    showMobile?: InjectableCss;
    itemNumberGrid?: GridItemProps;
    searchItemImgGrid?: GridItemProps;
    returnButtonMobile?: ButtonPresentationProps;
    backArrowButtonContainer?: InjectableCss;
    adiLogo?: InjectableCss;
    warningInfoContainer?: InjectableCss;
    warningInfoIcon?: InjectableCss;
    warningInfoText?: TypographyProps;
    jobNameContainer?: GridItemProps;
    btnExportToXten?: InjectableCss;
}

export const adiOrderDetailsContainerStyles: ADIOrderDetailsContainerStyles = {
    loadingOverlay: {
        css: css`
            & > div:first-child[data-loading="true"] {
                position: fixed;
            }
        `,
    },
    adiLogo: {
        css: css`
            img {
                display: none;
                @media print {
                    display: block;
                    width: 100px;
                    height: auto;
                    margin-bottom: 20px;
                    filter: brightness(1) invert(1);
                }
            }
        `,
    },
    backArrowButtonContainer: {
        css: css`
            padding: 12px 15px;
            display: flex;
            justify-content: flex-end;
        `,
    },
    returnButtonMobile: {
        variant: "secondary",
        css: css`
            width: auto;
            min-width: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            border: solid 1px ${getColor("secondary")};
            background: ${getColor("common.background")};
            padding: 0 12px;
            display: none;
            svg {
                height: 13px;
                width: 16px;
            }
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }

            @media only screen and (max-width: 1024px) {
                display: flex;
            }
        `,
    },
    searchItemImgGrid: {
        width: 12,
        css: css`
            > ${LazyImageStyle} {
                width: 55px;
                margin-right: 20px;
                height: 55px;
                max-width: 55px;
                img {
                    width: 100%;
                    height: 100%;
                    max-height: 55px;
                    max-width: 55px;
                    object-fit: contain;
                }
            }
            > div[class^="sc"] {
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1px solid ${getColor("common.border")};
                border-radius: 5px;
                width: 55px;
                margin-right: 20px;
                height: 55px;
                max-width: 55px;
                > ${TypographyStyle} {
                    font-size: 10px;
                    text-overflow: ellipsis;
                    max-width: calc(100% - 10px);
                    overflow: hidden;
                    display: block;
                    white-space: nowrap;
                    text-align: center;
                    color: ${getColor("text.main")};
                }
            }
            > ${TypographyStyle} {
                border: solid 1px ${getColor("common.border")};
                display: inline-flex;
                justify-content: center;
                align-items: center;
                flex-direction: column;
                font-size: 10px;
                line-height: 14px;
                color: ${getColor("text.main")};
                border-radius: 5px;
                width: 55px;
                margin-right: 20px;
                height: 55px;
                max-width: 55px;
            }
        `,
    },
    itemNumberGrid: {
        width: 12,
        css: css`
            padding-top: 12px;
            padding-bottom: 9px;
            justify-content: space-between;
            align-items: center;
            > ${TypographyStyle} {
                font-size: 14px;
                letter-spacing: 0.1px;
                text-decoration: none;
                padding-top: 2px;
                color: ${getColor("text.main")};
            }
        `,
    },
    hiddenMobile: {
        css: css`
            display: inline-block;
            @media only screen and (max-width: 576px) {
                display: none;
            }
        `,
    },
    showMobile: {
        css: css`
            display: none;
            @media only screen and (max-width: 576px) {
                display: inline-block;
            }
        `,
    },
    itemsMainContainer: {
        css: css`
            @media only screen and (max-width: 1024px) {
                &:last-child {
                    > ${GridOffset} {
                        margin-bottom: 0;
                    }
                }
                > ${GridOffset} {
                    line-height: 1;
                    background-color: ${getColor("common.background")};
                    margin-bottom: 30px;
                }
            }
        `,
    },
    firstSectionGridItem: {
        width: 12,
        css: css`
            padding-left: 0px;
            padding-right: 0px;
            margin: 0px 15px !important;
            border-bottom: 1px solid ${getColor("common.border")};
            justify-content: space-between;
            align-items: center;
            > ${StyledButton} {
                > ${TypographyStyle} {
                    font-size: 14px;
                    font-weight: bold;
                    letter-spacing: 0.1px;
                    text-decoration: none;
                    padding-top: 2px;
                }
            }
        `,
    },
    rowOne: {
        css: css`
            > ${TypographyStyle} {
                font-size: 16px;
                line-height: 14px;
                font-weight: bold;
                color: ${getColor("text.main")};
                text-transform: capitalize;
            }
        `,
    },
    customBorder: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            width: 100%;
            position: absolute;
            top: 27px;
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    orderAgainButton: {
        variant: "primary",
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            color: ${getColor("common.background")};
            &:focus {
                outline: none;
            }
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                flex-direction: row-reverse;
                margin-left: 10px;
                width: 48.64%;
            }
            @media screen and (min-width: 1025px) {
                max-width: 241px;
            }
        `,
    },
    returnItemsButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 22px;
            word-break: break-word;
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: bold;
            justify-content: center;
            border-radius: 4px;
            border: solid 1px ${getColor("secondary")};
            background: ${getColor("common.background")};
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                margin-top: 0px;
                margin-right: 10px;
                width: 48.64%;
            }
            @media screen and (min-width: 1025px) {
                max-width: 241px;
            }
        `,
    },
    dateContainer: { width: 2 },
    poNumberContainer: { width: 2 },
    referenceNumberContainer: { width: 2 },
    jobNameContainer: { width: 2 },
    statusContainer: { width: 2 },
    itemsContainer: { width: 1 },
    totalContainer: { width: 1 },
    billingInfoContainer: {
        width: [6, 6, 6, 6, 2],
        printWidth: 4,
        css: css`
            @media only screen and (max-width: 1024px) {
                padding-left: 0px;
                padding-right: 12px;
                > div[class^="Wrapper"] {
                    background: ${getColor("common.background")};
                    padding: 20px 20px 24px;
                    min-height: 145px;
                    height: 100%;
                    @media only screen and (max-width: 576px) {
                        min-height: 190px;
                    }
                }
            }
        `,
    },
    shippingInfoContainer: {
        width: [6, 6, 6, 6, 2],
        printWidth: 4,
        css: css`
            @media only screen and (max-width: 1024px) {
                padding-right: 0px;
                padding-left: 12px;
                > div[class^="Wrapper"] {
                    background: ${getColor("common.background")};
                    padding: 15px;
                    min-height: 145px;
                    @media only screen and (max-width: 576px) {
                        min-height: 190px;
                    }
                }
            }
        `,
    },
    paymentMethodContainer: {
        width: [0, 0, 0, 0, 2],
        printWidth: 4,
    },
    buttonsContainer: {
        width: [12, 12, 12, 12, 6],
        printWidth: 0,
        css: css`
            align-items: flex-end;
            flex-direction: column;
            @media screen and (min-width: 576px) and (max-width: 1024px) {
                flex-direction: row-reverse;
                padding-bottom: 30px;
            }
            .btnExportToXten{
                width: 100%;                
                button {            
                    width: 100%;
                    margin: 0;
                }
                .btnExportButton {
                    margin-top:24px; 
                 }               
            }
        `,
    },
    deliveryInfoContainer: { width: 2 },
    deliveryInfoContainerMultiple: {
        width: 12,
        printWidth: 12,
        css: css`
            @media print {
                padding-right: 0px;
            }
        `,
    },
    deliveryInfoContainerMultipleMobile: { width: 12, printWidth: 12 },
    cartsContainer: { width: 7 },
    priceContainer: { width: 3 },
    firstOrderDetailsContainer: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    titleGridContainer: {
        width: [10, 10, 8, 3, 5],
        css: css`
            @media only screen and (max-width: 1024px) {
                flex-basis: 83.333333%;
                max-width: 63.333333%;
            }
        `,
    },
    printGridContainer: {
        width: [2, 2, 4, 9, 7],
        css: css`
            @media only screen and (max-width: 1024px) {
                flex-basis: 16.666667%;
                max-width: 16.666667%;
            }
        `,
    },
    firstSection: {
        width: 12,
        css: css`
            @media only screen and (max-width: 1024px) {
                order: 2;
            }
            > ${GridWrapper} {
                > ${GridOffset} {
                    border-bottom: 1px solid ${getColor("text.disabled")};
                    @media only screen and (max-width: 1024px) {
                        border-bottom: 0;
                        background: ${getColor("common.background")};
                    }
                    > ${GridItemStyle} {
                        &:last-child {
                            > div[class^="Wrapper"] {
                                > div[class^="sc"] {
                                    border-right: none;
                                }
                            }
                        }
                        > div[class^="Wrapper"] {
                            > div[class^="sc"] {
                                border-right: 2px solid ${getColor("common.accent")};
                            }
                        }
                    }
                }
            }
        `,
    },
    secondSection: {
        width: 12,
        printWidth: 12,
        css: css`
            @media only screen and (max-width: 1024px) {
                order: 3;
            }
        `,
    },
    thirdSection: {
        width: 12,
        css: css`
            @media only screen and (max-width: 1024px) {
                order: 1;
            }
            @media print {
                padding-right: 0px;
            }
        `,
    },
    secondSectionBox: {
        css: css`
            > ${GridOffset} {
                background: ${getColor("common.background")};
                @media only screen and (max-width: 1024px) {
                    background: transparent;
                }
            }
        `,
    },
    firstSectionBox: {
        css: css`
            display: block;
            > ${GridOffset} {
                background: ${getColor("common.background")};
            }
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    thirdSectionBoxMobile: {
        css: css`
            display: none;
            @media only screen and (max-width: 1024px) {
                display: block;
                > ${GridOffset} {
                    padding: 15px 30px 20px;
                }
            }
        `,
    },
    thirdSectionBox: {
        css: css`
            display: block;
            position: relative;
            > ${GridOffset} {
                background: ${getColor("common.background")};
            }
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    warningInfoContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: center;
            margin-top: 60px;
        `,
    },
    warningInfoIcon: {
        css: css`
            svg {
                margin-right: 10px;
                transform: scale(1.5);
            }
        `,
    },
    warningInfoText: {
        css: css`
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            font-weight: 800;
            line-height: 1.29;
            letter-spacing: normal;
            color: ${getColor("common.backgroundContrast")};
        `,
    },
};

const styles = adiOrderDetailsContainerStyles;

const ADIOrderDetailsContainer: React.FC<Props> = ({
    id,
    reorder,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    order,
    history,
    allowRMASubmissions,
    userRoles,
    isLoading,
}) => {
    const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
    const [isReturnItemsModalOpen, setIsReturnItemsModalOpen] = useState(false);
    const [showInitialView, setShowInitialView] = useState(true);
    const [showViewDetails, setViewDetails] = useState(true);
    const [indexNum, setIndexNum] = useState<number | undefined>();
    const [orderDetailsNotFound, setOrderDetailsNotFound] = useState(false);
    useEffect(() => {
        if (!order) {
            setOrderDetailsNotFound(true);
        } else {
            setOrderDetailsNotFound(false);
        }
    }, [order]);

    const redirectToCartPage = () => {
        history.push("/Cart");
    };

    const [orderId, setOrderId] = React.useState('');
    React.useEffect(() => {
        const currentUrl = location.search;
        const searchParams = new URLSearchParams(currentUrl);
        setOrderId(searchParams.get("orderNumber") || "");
    }, [location]);


    const hideInitialView = (index: number) => {
        setShowInitialView(false);
        setViewDetails(true);
        setIndexNum(index);
    };

    const hideMobileView = () => {
        setViewDetails(false);
        setShowInitialView(true);
    };

    const onReorderModalClose = () => {
        setIsReorderModalOpen(false);
    };

    const onClickReorder = () => {
        reorder({
            onSuccess: () => {
                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                setIsReorderModalOpen(true);
            },
        });
    };

    const onReturnItemsModalClose = () => {
        setIsReturnItemsModalOpen(false);
    };

    const onClickReturnItems = () => {
        setIsReturnItemsModalOpen(true);
    };

    const formatDate = (inputDate: any): string => {
        const date = new Date(inputDate);
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const year = date.getFullYear().toString();

        const formattedDate = `${month}/${day}/${year}`;
        return formattedDate;
    };

    const singleOrder = () => {
        const selectedOrder = [];
        let selectedOrderFromStorage: any = sessionStorage.getItem("selectedOrder");
        selectedOrderFromStorage = JSON.parse(selectedOrderFromStorage);

        if (!selectedOrderFromStorage) return;
        selectedOrder.push({
            id: selectedOrderFromStorage.id,
            creationDate: formatDate(selectedOrderFromStorage.properties.creationDate),
            erpOrderNumber: selectedOrderFromStorage.erpOrderNumber,
            webOrderNumber: selectedOrderFromStorage.webOrderNumber,
            customerPO: selectedOrderFromStorage.customerPO,
            status: selectedOrderFromStorage.statusDisplay,
            orderTotalDisplay: selectedOrderFromStorage.orderTotalDisplay,
            noOfOrderLine: selectedOrderFromStorage.noOfOrderLine,
            productLineItems: selectedOrderFromStorage.productLineItems,
            orderDate: formatDate(selectedOrderFromStorage.orderDate),
        });
        sessionStorage.setItem("selectedOrdersList", JSON.stringify(selectedOrder));
        history.push("/MyAccount/Returns/SelectProducts");
    };

    const multipleOrders = () => {
        history.push(
            `/MyAccount/Returns/SelectOrders?orderNum=${order?.erpOrderNumber ? order?.erpOrderNumber : order?.webOrderNumber
            }`,
        );
    };
    let formattedDate: any;
    if (order) {
        const inputDate = order ? new Date(order?.orderDate) : null;
        const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
        formattedDate = inputDate && inputDate.toLocaleDateString("en-US", options).replace(" ", ". ");
    }

    return (
        <>
            {showInitialView ? (
                <LoadingOverlay {...styles.loadingOverlay} loading={isLoading} spinnerProps={{ color: "primary" }}>
                    <StyledWrapper {...styles.adiLogo}>
                        <img src="data:image/svg+xml;base64,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" />
                    </StyledWrapper>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.titleGridContainer}>
                            <Zone contentId={id} zoneName="titleZone" />
                        </GridItem>
                        <GridItem {...styles.printGridContainer}>
                            <Zone contentId={id} zoneName="printZone" />
                        </GridItem>
                        <GridItem {...styles.firstSection}>
                            <GridContainer {...styles.firstSectionBox}>
                                <GridItem {...styles.dateContainer}>
                                    <Zone contentId={id} zoneName="dateZone" />
                                </GridItem>
                                <GridItem {...styles.poNumberContainer}>
                                    <Zone contentId={id} zoneName="poNumberZone" />
                                </GridItem>
                                <GridItem {...styles.referenceNumberContainer}>
                                    <Zone contentId={id} zoneName="referenceNumberZone" />
                                </GridItem>
                                <GridItem {...styles.jobNameContainer}>
                                    <Zone contentId={id} zoneName="JobNameZone" />
                                </GridItem>
                                <GridItem {...styles.statusContainer}>
                                    <Zone contentId={id} zoneName="statusZone" />
                                </GridItem>
                                <GridItem {...styles.itemsContainer}>
                                    <Zone contentId={id} zoneName="itemsZone" />
                                </GridItem>
                                <GridItem {...styles.totalContainer}>
                                    <Zone contentId={id} zoneName="totalZone" />
                                </GridItem>
                            </GridContainer>
                            <ADIOrderDetailsSummaryTableMobile formattedDate={formattedDate} order={order} />
                        </GridItem>
                        <GridItem {...styles.secondSection}>
                            <GridContainer {...styles.secondSectionBox}>
                                <GridItem {...styles.billingInfoContainer}>
                                    <Zone contentId={id} zoneName="billingInfo" />
                                </GridItem>
                                <GridItem {...styles.shippingInfoContainer}>
                                    <Zone contentId={id} zoneName="shippingInfo" />
                                </GridItem>
                                <GridItem {...styles.paymentMethodContainer}>
                                    <Zone contentId={id} zoneName="paymentMethod" />
                                </GridItem>
                                <GridItem {...styles.buttonsContainer}>
                                    {canReorderItems && order?.canAddToCart && (
                                        <Button
                                            onClick={onClickReorder}
                                            disabled={isReordering}
                                            {...styles.orderAgainButton}
                                        >
                                            {translate("Order Again")}
                                        </Button>
                                    )}
                                    {/* as per ADI request we are hideing this until return develpoment completed  */}
                                    {/* uncommenting the return code as per P21 changes */}
                                    {order?.properties["canReturnItems"] == "Y" && allowRMASubmissions && (
                                        <Button
                                            variant="secondary"
                                            onClick={onClickReturnItems}
                                            {...styles.returnItemsButton}
                                            disabled={localStorage.getItem("p21ContactID") ? false : true}
                                        >
                                            {translate("Return Items")}
                                        </Button>
                                    )}
                                    <ADIOrderDetailsReorderModal
                                        isReorderModalOpen={isReorderModalOpen}
                                        closeModal={onReorderModalClose}
                                        redirectToCartPage={redirectToCartPage}
                                    />
                                    <ADIOrderDetailsReturnItemsModal
                                        isReturnItemsModalOpen={isReturnItemsModalOpen}
                                        closeReturnModal={onReturnItemsModalClose}
                                        multipleOrders={multipleOrders}
                                        singleOrder={singleOrder}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.thirdSection}>
                            <GridContainer {...styles.thirdSectionBox}>
                                <GridItem {...styles.deliveryInfoContainerMultiple}>
                                    <Zone contentId={id} zoneName="deliveryInfoZoneMultiple" />
                                </GridItem>
                            </GridContainer>
                            <GridContainer {...styles.thirdSectionBoxMobile}>
                                {order?.invoiceOrderLines.map((shipment: any, index: number) => {
                                    const getShippingStatus = (shipment: any) => {
                                        let suStatus = "Shipped";
                                        if (
                                            shipment?.trackingList[0]?.shipActivities !== null &&
                                            shipment?.trackingList[0]?.shipActivities.length > 0
                                        ) {
                                            shipment?.trackingList[0]?.shipActivities.forEach((i: any) => {
                                                if (i?.statusText === "Delivered") {
                                                    suStatus = "Delivered";
                                                }
                                            });
                                            return suStatus;
                                        }
                                    };
                                    return (
                                        <>
                                            <GridContainer key={index} {...styles.itemsMainContainer}>
                                                <GridItem {...styles.firstSectionGridItem}>
                                                    <StyledWrapper {...styles.rowOne}>
                                                        <Typography>{formattedDate}</Typography>
                                                        {shipment?.suStatus !== "Shipped" ||
                                                            (shipment?.suStatus === "Shipped" &&
                                                                (shipment?.trackingList.length === 0 ||
                                                                    order?.properties?.isLTL === "Y") && (
                                                                    <Typography {...styles.hiddenMobile}>
                                                                        &nbsp;-&nbsp;{shipment?.suStatus}
                                                                    </Typography>
                                                                ))}
                                                        {shipment?.suStatus === "Shipped" &&
                                                            shipment?.trackingList.length > 0 &&
                                                            order?.properties?.isLTL !== "Y" && (
                                                                <Typography {...styles.hiddenMobile}>
                                                                    &nbsp;-&nbsp;{getShippingStatus(shipment)}
                                                                </Typography>
                                                            )}
                                                    </StyledWrapper>
                                                    <Link
                                                        onClick={() => {
                                                            hideInitialView(index);
                                                        }}
                                                    >
                                                        {translate("View Details")}
                                                    </Link>
                                                </GridItem>
                                                <GridItem {...styles.itemNumberGrid}>
                                                    {shipment?.invoiceOrderLines.length === 1 && (
                                                        <Typography>
                                                            {shipment?.invoiceOrderLines.length}
                                                            {translate(" Item")}
                                                        </Typography>
                                                    )}
                                                    {shipment?.invoiceOrderLines.length > 1 && (
                                                        <Typography>
                                                            {shipment?.invoiceOrderLines.length}
                                                            {translate(" Items")}
                                                        </Typography>
                                                    )}
                                                    {shipment?.suStatus != "Shipped" ||
                                                        (shipment?.suStatus == "Shipped" &&
                                                            (shipment?.trackingList.length == 0 ||
                                                                order?.properties?.isLTL == "Y") && (
                                                                <Typography {...styles.showMobile}>
                                                                    {shipment?.suStatus}
                                                                </Typography>
                                                            ))}
                                                    {shipment?.suStatus == "Shipped" &&
                                                        shipment?.trackingList.length > 0 &&
                                                        order?.properties?.isLTL != "Y" && (
                                                            <Typography {...styles.showMobile}>
                                                                {getShippingStatus(shipment)}
                                                            </Typography>
                                                        )}
                                                </GridItem>
                                                <GridItem {...styles.searchItemImgGrid}>
                                                    {shipment &&
                                                        shipment?.invoiceOrderLines.map(
                                                            (shipmentPackageLine: any, index: number) => (
                                                                <>
                                                                    {index < 4 && (
                                                                        <>
                                                                            {shipmentPackageLine?.properties?.smallImagePath?.indexOf(
                                                                                "SM_NotFound.jpg",
                                                                            ) === -1 && (
                                                                                    <LazyImage
                                                                                        src={
                                                                                            shipmentPackageLine?.properties
                                                                                                ?.smallImagePath
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            {shipmentPackageLine !== undefined &&
                                                                                shipmentPackageLine?.properties.smallImagePath?.indexOf(
                                                                                    "SM_NotFound.jpg",
                                                                                ) !== -1 && (
                                                                                    <StyledWrapper as="div">
                                                                                        <Typography>
                                                                                            {
                                                                                                shipmentPackageLine.productErpNumber
                                                                                            }
                                                                                        </Typography>
                                                                                    </StyledWrapper>
                                                                                )}
                                                                        </>
                                                                    )}
                                                                </>
                                                            ),
                                                        )}
                                                    {shipment?.invoiceOrderLines.length > 4 && (
                                                        <Typography as="p">
                                                            <Typography as="span">
                                                                +{shipment?.invoiceOrderLines.length - 4}
                                                            </Typography>
                                                            <Typography as="span">{translate("more")}</Typography>
                                                        </Typography>
                                                    )}
                                                </GridItem>
                                            </GridContainer>
                                        </>
                                    );
                                })}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </LoadingOverlay>
            ) : (
                showViewDetails && (
                    <>
                        <StyledWrapper {...styles.backArrowButtonContainer}>
                            <Button onClick={hideMobileView} {...styles.returnButtonMobile}>
                                <OrderDetailsArrowLeft />
                            </Button>
                        </StyledWrapper>
                        <GridContainer>
                            <GridItem {...styles.deliveryInfoContainerMultipleMobile}>
                                <ADIOrderDetailsProductInformation userRoles={userRoles} indexNum={indexNum} />
                            </GridItem>
                        </GridContainer>
                    </>
                )
            )}
            {orderDetailsNotFound && !isLoading && (
                <StyledWrapper {...styles.warningInfoContainer}>
                    <StyledWrapper {...styles.warningInfoIcon}>
                        <Icon src={WarningRedIcon} size={20} />{" "}
                    </StyledWrapper>
                    <Typography {...styles.warningInfoText}>{translate("Order record not available.")}</Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIOrderDetailsContainer)),
    definition: {
        group: "Order Details",
        displayName: "ADI Order Details Container",
    },
};

export default widgetModule;
